import api from '@monorepo/utils/apiRequest';

/**
 * The agent's HTTP surface, in one place because two screens talk to it.
 *
 * ### Events are cursored on an id, never on time
 *
 * `after` is the last event id the client holds. Ids are monotonic within a
 * run; timestamps are not unique — two events can land in the same second,
 * which makes a time cursor either skip one or replay it.
 *
 * The run comes back alongside the events from the same request. Polling them
 * separately races: a client can read the final events, then read a status
 * that has not been committed yet, and sit there showing a spinner on a run
 * that finished.
 */

export const getConfig = () => api.get('/agent/config');

export const startRun = ({ instruction, readOnly = false, maxSteps }) =>
  api.post('/agent/runs', {
    instruction,
    read_only: readOnly,
    ...(maxSteps ? { max_steps: maxSteps } : {}),
  });

export const listRuns = () => api.get('/agent/runs');

export const getRun = (id) => api.get(`/agent/runs/${id}`);

export const getEvents = (id, after = 0) =>
  api.get(`/agent/runs/${id}/events?after=${after}`);

export const interruptRun = (id) => api.post(`/agent/runs/${id}/interrupt`, {});

export const pauseRun = (id) => api.post(`/agent/runs/${id}/pause`, {});

export const resumeRun = (id) => api.post(`/agent/runs/${id}/resume`, {});

/**
 * Add a message to a run in progress.
 *
 * Resolves with `pending: true` — it has NOT been delivered yet. A `tool_use`
 * block must be followed immediately by its `tool_result`, so the server
 * queues the text and applies it at the next turn boundary. The UI says so
 * rather than echoing it as sent, because appearing to swallow what someone
 * typed is worse than a short wait they can see.
 */
export const injectMessage = (id, text) =>
  api.post(`/agent/runs/${id}/inject`, { text });

/**
 * Carry on a run that has already finished.
 *
 * The counterpart to `injectMessage`, and NOT interchangeable with it:
 * injecting queues for a turn boundary that is coming anyway, so it resolves
 * `pending: true`. This makes a boundary — the text is appended to the
 * transcript and the run goes back on the queue — so it resolves with the run
 * itself, already back at `queued`.
 *
 * 409s with a reason worth showing: a run still going wants `injectMessage`, a
 * FAILED run cannot be continued at all (its transcript can end on a
 * `tool_use` with no `tool_result`, which the API refuses), and a run that
 * never started has no conversation to carry on.
 */
export const continueRun = (id, text) =>
  api.post(`/agent/runs/${id}/continue`, { text });

export const TERMINAL = ['done', 'failed', 'interrupted'];

export const isTerminal = (run) => !!run && TERMINAL.includes(run.status);

/** Running or queued — i.e. the agent still holds it. */
export const isLive = (run) => !!run && !isTerminal(run) && run.status !== 'paused';

/**
 * Can this run be carried on?
 *
 * `failed` is the one terminal status that cannot, and the UI has to know
 * rather than offer a box that 409s: a failure can leave the transcript
 * ending on a `tool_use` with no `tool_result`, which nothing outside the run
 * can repair because the tool never ran.
 */
export const isContinuable = (run) =>
  !!run && isTerminal(run) && run.status !== 'failed';
